'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.0.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initMap();
		this.initDatatable();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 *
	 * Find all links to images and use GLightbox to display them.
	 */
	initImageLinks() {
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);

		if (!parentNode) return;

		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			let figcaption = link.parentNode.querySelector('figcaption');
			let caption = figcaption ? figcaption.textContent : '';

			link.addEventListener('click', e => {
				e.preventDefault();

				const lightbox = GLightbox();
				lightbox.setElements([
					{
						href: link.href,
						type: 'image',
						title: caption
					}
				]);

				lightbox.open();
			});
		});
	}

	/**
	 * Init image map features
	 */
	initMap() {
		const map = document.getElementById('map');
		if (!map) return;
		const map_bg = map.querySelector('.map__bg');
		const map_bg_inner = map.querySelector('.map__bg-inner');
		const map_areas = map.querySelectorAll('area');
		const chapter_select = document.getElementById('chapter');
		map_areas.forEach(map_area => {
			map_area.addEventListener('mouseover', e => {
				const map_area_num = parseInt(e.target.getAttribute('data-num'));
				map_bg.setAttribute('style', 'background-position: calc(' + map_area_num + ' * (100% / 12)) top');
			});
			map_area.addEventListener('mouseout', e => {
				map_bg.removeAttribute('style');
			});
			map_area.addEventListener('click', e => {
				e.preventDefault();
				const map_area_num = parseInt(e.target.getAttribute('data-num'));
				const map_area_id = parseInt(e.target.getAttribute('data-id'));
				if (chapter_select.value == map_area_id) {
					map_bg_inner.setAttribute('class', 'map__bg-inner');
					chapter_select.value = '';
					return;
				}
				map_bg_inner.setAttribute('class', 'map__bg-inner map__bg-inner--' + map_area_num);
				chapter_select.value = map_area_id;
			});
		});
		chapter_select.addEventListener('change', e => {
			const map_area_id = parseInt(e.target.value);
			if (map_area_id) {
				const area = document.querySelector('area[data-id="' + map_area_id + '"]');
				if (area) {
					const map_area_num = parseInt(area.getAttribute('data-num'));
					map_bg_inner.setAttribute('class', 'map__bg-inner map__bg-inner--' + map_area_num);
					return;
				}
			}
			map_bg_inner.setAttribute('class', 'map__bg-inner');
		});
		window.onresize = window.setTimeout(this.fixImageMaps, 200);
		window.onload = this.fixImageMaps;
	}

	/**
	 * Adjust imagemaps for current dimensions
	 *
	 * @see {@link https://gist.github.com/guregu/e9f9caaed4b264a9214799bf03a06946}
	 */
	fixImageMaps() {
		var imgs = document.querySelectorAll("img[usemap]");
		[].forEach.call(imgs, function(img) {
			if (!img.naturalHeight) { return; }
			var h = img.height / img.naturalHeight;
			var w = img.width / img.naturalWidth;
			var map = document.getElementsByName(img.useMap.slice(1))[0];
			if (!map) { return; }
			for (var i = 0; i < map.children.length; i++) {
				var area = map.children[i];
				if (!area.coords) { continue; }
				var coords = area.coords;
				if (!area.originalCoords) {
					area.originalCoords = coords;
				} else {
					coords = area.originalCoords;
				}
				var split = coords.split(",")
				var fixed = "";
				split.forEach(function(coord, n) {
					if (n != 0) { fixed += ","; }
					fixed += n % 2 == 0 ? Number(coord) * w : Number(coord) * h;
				})
				area.coords = fixed;
			}
		});
	}

	/**
	 * Initialize datatable
	 */
	initDatatable() {

		// find email links
		const emails = document.querySelectorAll('.js-email');
		if (!emails.length) return;

		emails.forEach(email => {
			email.addEventListener('click', event => {
				event.preventDefault();

				// replace with text input
				const emailText = document.createElement('input');
				emailText.setAttribute('type', 'text');
				emailText.value = email.getAttribute('href').replace('mailto:', '')
				emailText.classList.add('w-full');
				emailText.setAttribute('style', 'min-width: ' + emailText.value.length + 'ch');
				email.classList.add('hidden');
				email.parentNode.appendChild(emailText);
				emailText.focus();
				emailText.select();
				try {
					document.execCommand('copy');
				} catch (err) {
					// unable to copy
				}
			});
		});
	}
}

window._site = new Site();
